import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Alert, Avatar, Button } from '@rallyshift/design-system'
import { ArrowLeft, Bell, BellOff, Flag, FileText, Mail, Paperclip, Send, Trash2, X } from 'lucide-react'
import api, { type Attachment, type Conversation, type Message } from '../../api/client'
import { useLocale } from '../../contexts/LocaleContext'
import { useRealtime } from '../../hooks/useRealtime'
import { ConversationIcon } from './ConversationList'
import { dayLabel, fileSize, messageTime, personName, sameDay } from './format'

const MAX_BODY = 4000
const MAX_ATTACHMENTS = 10
const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp']

interface Props {
  conversation: Conversation
  onChanged: () => void
}

interface Staged {
  key: string
  file: File
  preview: string | null
  attachment?: Attachment
  error?: string
}

interface Pending {
  client_id: string
  body: string
  attachments: Attachment[]
  email: boolean
  failed: boolean
}

function newClientId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`
}

const ConversationThread: React.FC<Props> = ({ conversation, onChanged }) => {
  const { t, locale } = useLocale()
  const [messages, setMessages] = useState<Message[]>([])
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(true)
  const [loadingOlder, setLoadingOlder] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [body, setBody] = useState('')
  const [staged, setStaged] = useState<Staged[]>([])
  const [pending, setPending] = useState<Pending[]>([])
  const [muted, setMuted] = useState(conversation.muted)
  const [emailToo, setEmailToo] = useState(false)
  const scroller = useRef<HTMLDivElement>(null)
  const stickToBottom = useRef(true)
  const keepOffset = useRef<number | null>(null)
  const fileInput = useRef<HTMLInputElement>(null)
  const id = conversation.id

  useEffect(() => setMuted(conversation.muted), [conversation.muted])

  const markRead = useCallback(
    (list: Message[]) => {
      const last = list[list.length - 1]
      if (!last || document.visibilityState === 'hidden') return
      api.messaging.markRead(id, last.id).then(onChanged).catch(() => undefined)
    },
    [id, onChanged],
  )

  const loadLatest = useCallback(async () => {
    try {
      const page = await api.messaging.messages(id)
      setMessages(page.messages)
      setHasMore(page.has_more)
      setError(null)
      stickToBottom.current = true
      markRead(page.messages)
    } catch (err: any) {
      setError(err?.message || '')
    } finally {
      setLoading(false)
    }
  }, [id, markRead])

  useEffect(() => {
    setLoading(true)
    setMessages([])
    setPending([])
    setStaged([])
    setBody('')
    setNotice(null)
    loadLatest()
  }, [loadLatest])

  useEffect(() => {
    const onVisible = () => document.visibilityState === 'visible' && markRead(messages)
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [markRead, messages])

  useRealtime((event) => {
    if (event.type === 'resync') {
      loadLatest()
      return
    }
    if (event.conversation_id !== id) return
    if (event.type === 'message.created') {
      const incoming = event.message
      setPending((list) => list.filter((p) => p.client_id !== incoming.client_id))
      setMessages((list) => {
        if (list.some((m) => m.id === incoming.id)) return list
        const next = [...list, incoming]
        if (!incoming.is_mine) markRead(next)
        return next
      })
    } else if (event.type === 'message.deleted') {
      setMessages((list) =>
        list.map((m) => (m.id === event.message_id ? { ...m, deleted: true, body: '', attachments: [] } : m)),
      )
    }
  })

  // Stay pinned to the newest message unless the reader has scrolled up, and keep the
  // view still when older messages are added above it.
  useLayoutEffect(() => {
    const el = scroller.current
    if (!el) return
    if (keepOffset.current !== null) {
      el.scrollTop = el.scrollHeight - keepOffset.current
      keepOffset.current = null
    } else if (stickToBottom.current) {
      el.scrollTop = el.scrollHeight
    }
  }, [messages, pending])

  const onScroll = () => {
    const el = scroller.current
    if (!el) return
    stickToBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80
  }

  const loadOlder = async () => {
    if (!messages[0]) return
    setLoadingOlder(true)
    try {
      const page = await api.messaging.messages(id, { before: messages[0].id })
      keepOffset.current = scroller.current ? scroller.current.scrollHeight - scroller.current.scrollTop : null
      setMessages((list) => [...page.messages.filter((m) => !list.some((x) => x.id === m.id)), ...list])
      setHasMore(page.has_more)
    } catch (err: any) {
      setError(err?.message || '')
    } finally {
      setLoadingOlder(false)
    }
  }

  const addFiles = (files: FileList | null) => {
    if (!files) return
    const room = MAX_ATTACHMENTS - staged.length
    const picked = Array.from(files).slice(0, room)
    for (const file of picked) {
      const isImage = IMAGE_TYPES.includes(file.type)
      const key = newClientId()
      const tooLarge = file.size > (isImage ? 10 : 20) * 1024 * 1024
      const entry: Staged = {
        key,
        file,
        preview: isImage && typeof URL.createObjectURL === 'function' ? URL.createObjectURL(file) : null,
        error: tooLarge ? t('messages.tooLarge') : undefined,
      }
      setStaged((list) => [...list, entry])
      if (tooLarge) continue
      api.messaging
        .upload(id, file)
        .then((attachment) =>
          setStaged((list) => list.map((s) => (s.key === key ? { ...s, attachment } : s))),
        )
        .catch((err: any) =>
          setStaged((list) => list.map((s) => (s.key === key ? { ...s, error: err?.message || 'Error' } : s))),
        )
    }
    if (fileInput.current) fileInput.current.value = ''
  }

  const unstage = (key: string) =>
    setStaged((list) => {
      const gone = list.find((s) => s.key === key)
      if (gone?.preview) URL.revokeObjectURL(gone.preview)
      return list.filter((s) => s.key !== key)
    })

  const uploading = staged.some((s) => !s.attachment && !s.error)
  const ready = staged.filter((s) => s.attachment).map((s) => s.attachment!)
  const canSend = !uploading && (body.trim().length > 0 || ready.length > 0) && body.length <= MAX_BODY

  const deliver = async (p: Pending) => {
    setPending((list) => list.map((x) => (x.client_id === p.client_id ? { ...x, failed: false } : x)))
    try {
      const message = await api.messaging.send(id, {
        body: p.body,
        attachment_ids: p.attachments.map((a) => a.id),
        client_id: p.client_id,
        ...(p.email ? { email: true } : {}),
      })
      setPending((list) => list.filter((x) => x.client_id !== p.client_id))
      setMessages((list) => (list.some((m) => m.id === message.id) ? list : [...list, message]))
      onChanged()
    } catch {
      setPending((list) => list.map((x) => (x.client_id === p.client_id ? { ...x, failed: true } : x)))
    }
  }

  const send = () => {
    if (!canSend) return
    const p: Pending = { client_id: newClientId(), body: body.trim(), attachments: ready, email: emailToo, failed: false }
    setPending((list) => [...list, p])
    setBody('')
    setEmailToo(false)
    staged.forEach((s) => s.preview && URL.revokeObjectURL(s.preview))
    setStaged([])
    stickToBottom.current = true
    deliver(p)
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault()
      send()
    }
  }

  const remove = async (m: Message) => {
    if (!window.confirm(t('messages.deleteConfirm'))) return
    try {
      await api.messaging.deleteMessage(m.id)
      setMessages((list) => list.map((x) => (x.id === m.id ? { ...x, deleted: true, body: '', attachments: [] } : x)))
      onChanged()
    } catch (err: any) {
      setError(err?.message || 'Error')
    }
  }

  const report = async (m: Message) => {
    if (!window.confirm(t('messages.reportConfirm'))) return
    try {
      await api.messaging.report(m.id)
      setNotice(t('messages.reported'))
    } catch (err: any) {
      setError(err?.message || 'Error')
    }
  }

  const removeSender = async (m: Message) => {
    if (!window.confirm(t('messages.removeConfirm'))) return
    try {
      await api.messaging.removeFromChannel(id, m.sender.id)
      setNotice(`${personName(m.sender)}: ${t('messages.removeFromChannel')} ✓`)
    } catch (err: any) {
      setError(err?.message || 'Error')
    }
  }

  const toggleMute = async () => {
    const next = !muted
    setMuted(next)
    try {
      await api.messaging.setMuted(id, next)
      onChanged()
    } catch {
      setMuted(!next)
    }
  }

  const kindLabel =
    conversation.kind === 'organization'
      ? t('messages.kindOrganization')
      : conversation.kind === 'event'
      ? t('messages.kindEvent')
      : conversation.kind === 'shift'
        ? t('messages.kindShift')
        : t('messages.kindDirect')
  const canEmail = conversation.kind === 'organization' || conversation.kind === 'event'
  const accept = conversation.can_attach_files ? `${IMAGE_TYPES.join(',')},application/pdf` : IMAGE_TYPES.join(',')

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="flex items-center gap-3 border-b border-border px-4 py-3">
        <Link
          to="/messages"
          className="-ml-1 flex min-h-touch-min min-w-touch-min items-center justify-center text-text-secondary hover:text-text-primary lg:hidden"
          aria-label={t('messages.back')}
        >
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <ConversationIcon conversation={conversation} size={36} />
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-md font-semibold text-text-primary">{conversation.title}</h2>
          <p className="truncate text-xs text-text-muted">
            {[kindLabel, conversation.subtitle].filter(Boolean).join(' · ')}
          </p>
        </div>
        <Button
          variant="ghost"
          iconOnly
          aria-label={muted ? t('messages.unmute') : t('messages.mute')}
          title={muted ? t('messages.unmute') : t('messages.mute')}
          onClick={toggleMute}
          icon={muted ? <BellOff className="h-5 w-5" /> : <Bell className="h-5 w-5" />}
        />
      </header>

      <div ref={scroller} onScroll={onScroll} className="min-h-0 flex-1 overflow-y-auto px-4 py-4" data-testid="thread">
        {error !== null && <Alert variant="danger">{error || t('messages.error')}</Alert>}
        {notice && (
          <div className="mb-3">
            <Alert variant="success">{notice}</Alert>
          </div>
        )}
        {hasMore && (
          <div className="mb-4 text-center">
            <Button variant="secondary" onClick={loadOlder} loading={loadingOlder}>
              {t('messages.loadOlder')}
            </Button>
          </div>
        )}
        {loading && <p className="text-center text-sm text-text-muted">{t('common.loading')}</p>}

        <ol className="space-y-1">
          {messages.map((m, i) => {
            const prev = messages[i - 1]
            const newDay = !prev || !sameDay(prev.created_at, m.created_at)
            const grouped = !newDay && prev && prev.sender.id === m.sender.id && !prev.deleted
            return (
              <React.Fragment key={m.id}>
                {newDay && (
                  <li className="py-3 text-center text-xs font-semibold text-text-muted" aria-hidden>
                    {dayLabel(m.created_at, locale)}
                  </li>
                )}
                <MessageRow
                  message={m}
                  grouped={!!grouped}
                  showSender={conversation.kind !== 'direct'}
                  canModerate={conversation.can_moderate}
                  onDelete={remove}
                  onReport={report}
                  onRemoveSender={removeSender}
                />
              </React.Fragment>
            )
          })}
          {pending.map((p) => (
            <li key={p.client_id} className="flex justify-end">
              <div className="max-w-[75%] rounded-lg bg-tenant-soft px-3 py-2 text-sm text-text-primary opacity-70">
                {p.body && <p className="whitespace-pre-wrap break-words">{p.body}</p>}
                {p.attachments.length > 0 && <Attachments attachments={p.attachments} />}
                <p className="mt-1 text-right text-xs text-text-muted">
                  {p.failed ? (
                    <>
                      {t('messages.sendFailed')}{' '}
                      <button type="button" className="font-semibold underline" onClick={() => deliver(p)}>
                        {t('messages.retry')}
                      </button>
                    </>
                  ) : (
                    t('messages.sending')
                  )}
                </p>
              </div>
            </li>
          ))}
        </ol>
      </div>

      {conversation.can_post ? (
        <div className="border-t border-border p-3">
          {staged.length > 0 && (
            <ul className="mb-2 flex flex-wrap gap-2">
              {staged.map((s) => (
                <li key={s.key} className="relative">
                  {s.preview ? (
                    <img src={s.preview} alt={s.file.name} className="h-16 w-16 rounded-md object-cover" />
                  ) : (
                    <span className="flex h-16 max-w-[180px] items-center gap-2 rounded-md border border-border px-2 text-xs text-text-secondary">
                      <FileText className="h-5 w-5 shrink-0" />
                      <span className="truncate">{s.file.name}</span>
                    </span>
                  )}
                  {!s.attachment && !s.error && (
                    <span className="absolute inset-0 flex items-center justify-center rounded-md bg-surface-card/70 text-xs text-text-secondary">
                      …
                    </span>
                  )}
                  {s.error && (
                    <span className="absolute inset-0 flex items-center justify-center rounded-md bg-status-danger-bg/90 p-1 text-center text-[10px] leading-tight text-status-danger-text" title={s.error}>
                      {t('messages.uploadFailed')}
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => unstage(s.key)}
                    aria-label={t('common.delete')}
                    className="absolute -right-1.5 -top-1.5 rounded-full bg-surface-inverse p-0.5 text-white"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="flex items-end gap-2">
            <input
              ref={fileInput}
              type="file"
              multiple
              accept={accept}
              className="hidden"
              data-testid="file-input"
              onChange={(e) => addFiles(e.target.files)}
            />
            <Button
              variant="ghost"
              iconOnly
              aria-label={conversation.can_attach_files ? t('messages.attachFile') : t('messages.attachPhoto')}
              title={conversation.can_attach_files ? t('messages.attachFile') : t('messages.attachPhoto')}
              onClick={() => fileInput.current?.click()}
              disabled={staged.length >= MAX_ATTACHMENTS}
              icon={<Paperclip className="h-5 w-5" />}
            />
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              onKeyDown={onKeyDown}
              onPaste={(e) => e.clipboardData.files.length > 0 && addFiles(e.clipboardData.files)}
              placeholder={t('messages.placeholder')}
              aria-label={t('messages.placeholder')}
              rows={1}
              maxLength={MAX_BODY}
              className="max-h-40 min-h-[44px] flex-1 resize-none rounded-md border border-border bg-surface-card px-3 py-2.5 text-sm text-text-primary focus:border-tenant-brand focus:outline-none"
            />
            <Button
              variant="tenant"
              iconOnly
              aria-label={t('messages.send')}
              onClick={send}
              disabled={!canSend}
              icon={<Send className="h-5 w-5" />}
            />
          </div>
          {canEmail && (
            <label className="mt-2 flex items-center gap-2 text-sm text-text-secondary">
              <input
                type="checkbox"
                checked={emailToo}
                onChange={(e) => setEmailToo(e.target.checked)}
                className="h-4 w-4 rounded border-border"
              />
              {t('messages.alsoEmail')}
            </label>
          )}
        </div>
      ) : (
        <p className="border-t border-border px-4 py-3 text-center text-sm text-text-muted">{t('messages.readOnly')}</p>
      )}
    </div>
  )
}

interface RowProps {
  message: Message
  grouped: boolean
  showSender: boolean
  canModerate: boolean
  onDelete: (m: Message) => void
  onReport: (m: Message) => void
  onRemoveSender: (m: Message) => void
}

const MessageRow: React.FC<RowProps> = ({ message: m, grouped, showSender, canModerate, onDelete, onReport, onRemoveSender }) => {
  const { t } = useLocale()
  const mine = m.is_mine
  const name = personName(m.sender)

  return (
    <li className={`group flex gap-2 ${mine ? 'justify-end' : 'justify-start'} ${grouped ? '' : 'pt-2'}`}>
      {!mine && showSender && (
        <div className="w-8 shrink-0">{!grouped && <Avatar name={name} src={m.sender.avatar_url} size={32} />}</div>
      )}
      <div className={`flex max-w-[75%] flex-col ${mine ? 'items-end' : 'items-start'}`}>
        {!mine && showSender && !grouped && (
          <p className="mb-0.5 text-xs font-semibold text-text-secondary">
            {name}
            {m.sender.is_organiser && <span className="ml-1 font-normal text-text-muted">· {t('messages.organiser')}</span>}
          </p>
        )}
        <div className="flex items-center gap-1">
          {mine && <Actions message={m} canModerate={canModerate} onDelete={onDelete} onReport={onReport} onRemoveSender={onRemoveSender} />}
          <div
            className={`rounded-lg px-3 py-2 text-sm ${
              m.deleted
                ? 'border border-dashed border-border italic text-text-muted'
                : mine
                  ? 'bg-tenant-soft text-text-primary'
                  : 'bg-surface-sunken text-text-primary'
            }`}
          >
            {m.deleted ? (
              t('messages.deleted')
            ) : (
              <>
                {m.body && <p className="whitespace-pre-wrap break-words">{m.body}</p>}
                {m.attachments.length > 0 && <Attachments attachments={m.attachments} />}
              </>
            )}
            <p className="mt-1 text-right text-[11px] text-text-muted">
              {messageTime(m.created_at)}
              {m.emailed && (
                <span className="ml-2 inline-flex items-center gap-1" title={t('messages.emailed')}>
                  <Mail className="h-3 w-3" aria-hidden />
                  {t('messages.emailed')}
                </span>
              )}
              {!!m.report_count && (
                <span className="ml-2 font-semibold text-status-danger-text">
                  {m.report_count}× {t('messages.reports')}
                </span>
              )}
            </p>
          </div>
          {!mine && <Actions message={m} canModerate={canModerate} onDelete={onDelete} onReport={onReport} onRemoveSender={onRemoveSender} />}
        </div>
      </div>
    </li>
  )
}

const Actions: React.FC<Omit<RowProps, 'grouped' | 'showSender'>> = ({ message: m, canModerate, onDelete, onReport, onRemoveSender }) => {
  const { t } = useLocale()
  if (m.deleted) return null
  const btn =
    'rounded p-1 text-text-muted opacity-0 transition-opacity hover:bg-surface-sunken hover:text-text-primary focus:opacity-100 group-hover:opacity-100'
  return (
    <div className="flex shrink-0 items-center">
      {(m.is_mine || canModerate) && (
        <button type="button" className={btn} onClick={() => onDelete(m)} aria-label={t('messages.delete')} title={t('messages.delete')}>
          <Trash2 className="h-4 w-4" />
        </button>
      )}
      {!m.is_mine && (
        <button type="button" className={btn} onClick={() => onReport(m)} aria-label={t('messages.report')} title={t('messages.report')}>
          <Flag className="h-4 w-4" />
        </button>
      )}
      {!m.is_mine && canModerate && !m.sender.is_organiser && (
        <button
          type="button"
          className={btn}
          onClick={() => onRemoveSender(m)}
          aria-label={t('messages.removeFromChannel')}
          title={t('messages.removeFromChannel')}
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  )
}

const Attachments: React.FC<{ attachments: Attachment[] }> = ({ attachments }) => {
  const { t } = useLocale()
  const images = attachments.filter((a) => a.kind === 'image')
  const files = attachments.filter((a) => a.kind === 'file')
  return (
    <div className="mt-1 space-y-1">
      {images.length > 0 && (
        <div className={`grid gap-1 ${images.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}>
          {images.map((a) => (
            <a
              key={a.id}
              href={a.url}
              target="_blank"
              rel="noreferrer"
              aria-label={t('messages.photo')}
              className="block overflow-hidden rounded-md"
            >
              <img
                src={a.thumbnail_url ?? a.url}
                alt=""
                loading="lazy"
                width={a.width ?? undefined}
                height={a.height ?? undefined}
                className="max-h-64 w-full object-cover"
              />
            </a>
          ))}
        </div>
      )}
      {files.map((a) => (
        <a
          key={a.id}
          href={a.url}
          target="_blank"
          rel="noreferrer"
          className="flex items-center gap-2 rounded-md border border-border bg-surface-card px-3 py-2 text-text-primary hover:bg-surface-sunken"
        >
          <FileText className="h-5 w-5 shrink-0 text-status-danger-text" />
          <span className="min-w-0 flex-1 truncate font-semibold">{a.name ?? 'document.pdf'}</span>
          <span className="shrink-0 text-xs text-text-muted">{fileSize(a.size)}</span>
        </a>
      ))}
    </div>
  )
}

export default ConversationThread
